import dayjs from '~/timeInit';

import type { CppInvoiceTransaction } from '~/services/LuxLoyaltyService.types';

import { currencyCode, formatBandRates } from './invoiceFormat';

/** One CSV row, keyed by {@link transactionCsvHeaders}. */
export type TransactionCsvRow = Record<string, string | number>;

/**
 * Renders the CPP rate(s) a transaction was priced at.
 *
 * @returns The rate(s), e.g. `"1.2c"` or `"1.2c, 0.9c"`; empty when the row could not be priced.
 */
export function formatTransactionCpp(transaction: CppInvoiceTransaction): string {
  return formatBandRates(transaction.bands);
}

/** Formats a signed cents amount as a bare decimal for CSV, blank when unpriced. */
function csvAmount(amountCents: number | null): string {
  return amountCents === null ? '' : (amountCents / 100).toFixed(2);
}

/** A transaction the CSV can label with its own partner and currency. */
type CsvTransaction = CppInvoiceTransaction & { partnerName?: string; currency?: string | null };

export interface TransactionCsvOptions {
  /**
   * Names each row's currency instead of one heading for the whole file.
   *
   * Used when an all-partners export spans more than one currency, so a USD amount is not written
   * under a Cost (AUD) heading.
   */
  perRowCurrency?: boolean;
}

/** Cost column heading. A mixed file has no single currency, so the heading stays unqualified. */
function costHeader(currency: string | null, perRowCurrency: boolean): string {
  return perRowCurrency ? 'Cost' : `Cost (${currencyCode(currency)})`;
}

/**
 * Converts a page (or the whole period) of transactions into CSV rows.
 *
 * `partnerName` is the export-wide fallback. A row may carry its own `partnerName` when the export
 * covers more than one partner.
 *
 * @param transactions - The transactions to export.
 * @param partnerName - The partner the export is scoped to.
 * @param currency - The period's currency, used only for the cost column heading.
 * @param options - Set `perRowCurrency` when the rows are not all in `currency`.
 */
export function transactionsToCsvRows(
  transactions: Array<CsvTransaction>,
  partnerName: string,
  currency: string | null,
  options: TransactionCsvOptions = {},
): Array<TransactionCsvRow> {
  const perRowCurrency = Boolean(options.perRowCurrency);
  const header = costHeader(currency, perRowCurrency);

  return transactions.map((transaction) => ({
    'Transaction ID': transaction.transactionId,
    Date: transaction.date,
    // `date` is the service's own day in the program timezone. Rendering the time beside it in UTC
    // put the two a calendar day apart for anything after 10am Sydney — on the file a partner
    // reconciles a disputed invoice against. Both columns are now the same clock.
    Time: dayjs(transaction.createdAt).tz().format('HH:mm:ss'),
    Partner: transaction.partnerName || partnerName,
    Type: transaction.transactionType,
    Activity: transaction.activityTitle,
    Source: transaction.source,
    'Account Number': transaction.accountNumber,
    Points: transaction.signedPoints,
    'Bonus Points': transaction.signedBonusPoints,
    'Status Credits': transaction.statusCredits,
    CPP: formatTransactionCpp(transaction),
    'Reverses Transaction ID': transaction.originalTransactionId ?? '',
    ...(perRowCurrency ? { Currency: transaction.currency?.trim() || '' } : {}),
    [header]: csvAmount(transaction.amountCents),
  }));
}

/**
 * Column order of the exported activity statement, with the cost column named for the period's
 * currency.
 *
 * This CSV is the backup attachment that accompanies a partner invoice, so it deliberately carries
 * more than the on-screen grid: the ledger ids and the customer/account the points landed on are
 * what a partner dispute is actually reconciled with.
 *
 * It deliberately carries LESS in one respect: how a row was priced (its rate card and the day it
 * was priced against) and why a row carries no cost (its cost status, exclusion reason and cost
 * source) are internal pricing mechanics, and stay in the admin grid rather than going out to a
 * partner. `CPP` and a blank `Cost` are all the statement itself needs to show.
 *
 * The single source of column order — {@link transactionsToCsvRows} builds its rows to match.
 *
 * @param currency - The period's currency.
 * @param options - Set `perRowCurrency` when the rows are not all in `currency`.
 */
export function transactionCsvHeaders(currency: string | null, options: TransactionCsvOptions = {}): Array<string> {
  const perRowCurrency = Boolean(options.perRowCurrency);

  return [
    'Transaction ID',
    'Date',
    'Time',
    'Partner',
    'Type',
    'Activity',
    'Source',
    'Account Number',
    'Points',
    'Bonus Points',
    'Status Credits',
    'CPP',
    'Reverses Transaction ID',
    ...(perRowCurrency ? ['Currency'] : []),
    costHeader(currency, perRowCurrency),
  ];
}
